import { useFormik } from "formik";
import React, { useEffect, useMemo } from "react";
import * as Yup from "yup";
import { useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import TextInput from "../../common/textfield/TextInput";
import SelectFiled from "../../common/textfield/SelectFiled";
import { Button } from "../../common/Button";
import { CircularProgress, Skeleton } from "@mui/material";
import { usePatientsBySOPQuery } from "../../../hooks/ReactQueryHooks/usePatientsBySOPQuery";
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { DateSelectorWithoutFormik } from "../../common/textfield/DateSelector";
import moment from "moment";
import { getAppointmentRecord, bookAppointment, rescheduleAppointment, checkSchedule, getSlots, updateAppointmentRequest, getlistPractitioners, getEncounterRecord } from "../api/AppointmentRequests";
import {getScheduleSlots, getSlotById, updateScheduleSlot} from "../../api/Schedule";
import { getPatientData } from '../../api/Individual';
import { failed, success } from "../../common/Toastify";
import "./style.css"
import { useSelector } from "react-redux";
import { getSignature } from "../../Zoom/functions";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { editQuestionnaireResponse, createQuestionnaireResponse, getQuestionnaireResponseById } from "../../api/Questionnaire";
import { buildSessionTopic } from "../../../utils/ZoomSessionSupport";
import { validation, conditionalValidation } from "../../../utils/constants";
import { generatePass } from "../AppointmentRequests/Constants";
import { LOCATIONS } from "../../common/location";
import {
    computeServiceCategoryAvailabilities,
    scheduleMatchesSelectedOrg,
} from "../../../utils/ScheduleAvailabilitySupport";
import environment from "../../../environment";

const SCHEDULE_SLOTS_BATCH_SIZE = environment.SCHEDULE_SLOTS_BATCH_SIZE;
const SCHEDULE_SLOTS_BATCH_DELAY_MS = environment.SCHEDULE_SLOTS_BATCH_DELAY_MS;
/**
 * Appointment
 * parameters:
 * modalShow: boolean
 * handleShow: function to execute if showing the component,
 * handleHide: function to execute if hiding component
 * type: string, type of appointment
 * encounterId: string encounterId if any
 * questionnaireResponseId: string ID of questionnaire response
 * patient: string, patientId
 * 
 * Universal, reusable appointment scheduling component.
 * Type can be 
 *      "intake" (making an appointment from a new call)
 *      "inprogress" (making an appointment from an existing encounter/appointment in progress, ie: a followup)
 *      "reschedule" (reschedule an existing appointment)
 * If making an appointment from an inprogress appointment/encounter, a NEW encounter is created, otherwise the 
 * existing encounter is used.
 * 
 * questionnaireResponseId, if provided, is used to retrieve questionnaire details from the intake questions. If it
 * ISN'T provided, it will attempt to extract the qrID from the encounter itself to get the intake questions
 * 
 * If patient is provided it will be used, otherewise, it will attempt to extract the patient from the encounter data.
 * 
 * Appointment details will be extracted from the encounter data, if it exists.
 */
const Appointment = ({ modalShow, handleShow, handleHide, type, encounterId, questionnaireResponseId, patient, selectedOrgId, onSubmitOverride, prefillTitle, reasonFieldLabel, isInternalReferral = false }) => {
    const effectiveReasonLabel = reasonFieldLabel ?? (isInternalReferral ? "Reason for referral" : "Reason for visit");
    const user = useSelector((state) => state?.auth?.user);
    const userOrg = useSelector((state) => state?.auth?.user?.organizations)
    const userOrgId = userOrg?.[0]?.id;
    const effectiveOrgId = selectedOrgId || userOrgId;
    let isReadOnly = true

    // state variables
    const [patientsList, setPatientsList] = useState([]);
    const [patientId, setPatientId] = useState(patient || null);
    const [patientData, setPatientData] = useState(null);
    const [encounterData, setEncounterData] = useState(null);
    const [appointmentId, setAppointmentId] = useState(null);
    const [appointmentData, setAppointmentData] = useState(null);
    const [qRId, setQRId] = useState(questionnaireResponseId);
    const [reason, setReason] = useState(null);
    const [intakeQuestions, setIntakeQuestions] = useState({});
    const [practitionerList, setPractitionerList] = useState([]);
    const [practitionerId, setPractitionerId] = useState(null);

    const [btnLoading, setBtnLoading] = useState(false);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState("");
    const [reservedCategories, setReservedCategories] = useState ({})
    const [serviceCategory, setServiceCategory] = useState([]);
    const [categoriesByDay, setCategoriesByDay] = useState({});
    const [availableSlots, setAvailableSlots] = useState([]);
    const [availableDays, setAvailableDays] = useState([]);

    const [slotUnavailable, setSlotUnavailable] = useState(false);
    const [scopeOfPractice, setScopeOfPractice] = useState(null);
    const [computedServiceCategories, setComputedServiceCategories] = useState([]);

    const monthOptions = useMemo(() => {
        const opts = [];
        const start = moment().startOf("month");
        for (let i = 0; i < 5; i++) {
            const m = moment(start).add(i, "months");
            opts.push({ value: m.format("YYYY-MM"), name: m.format("MMMM YYYY") });
        }
        return opts;
    }, []);

    const formik = useFormik({
        initialValues: {
            title: "",
            patient: "", 
            date: "",
            serviceCategory: "",
            availableSlot: "",
            preferredChannel: "",
            returnTelephoneNumber: "",
            returnTelephoneNumberExt: "",
            practitionerPreference: "",
            practitioner: "",
            schedulingMonth: moment().format("YYYY-MM"),
        },  
        validationSchema: Yup.object({
            title: conditionalValidation(
                Yup.string(),
                {
                    validation,
                    mandatory: true,
                    label: effectiveReasonLabel
                }),
            patient: conditionalValidation(
                Yup.string(),
                {
                    validation,
                    mandatory: (type === "intake" && !isInternalReferral) ? true : false,
                    label: "Patient"
                }),
            date: conditionalValidation(
                Yup.string(),
                {
                    validation,
                    label: "Date",
                    mandatory: true
                }),
            serviceCategory: conditionalValidation(
                Yup.string(),
                {
                    validation,
                    label: "Appointment Type",
                    mandatory: true
                }),
            availableSlot: Yup.object().required("Required slots field."),
            preferredChannel: conditionalValidation(
                Yup.string(),
                {
                    validation,
                    label: "Preferred channel",
                    mandatory: true
                }),
            returnTelephoneNumber: conditionalValidation(
                Yup.string().matches(/^(?:\+?1[-. ]?)?\(?[0-9]\d{2}\)?[-. ]?\d{3}[-. ]?\d{4}$/, "Enter Valid Return Telephone Number."),
                {
                    validation,
                    label: "Return Telephone Number",
                    mandatory: true
                }),
            returnTelephoneNumberExt: conditionalValidation(
                Yup.string().matches(/^\d{0,4}$/, 'Must be 4 digit or less.'),
                {
                    validation,
                    label: "Extension"
                }),
            practitionerPreference: conditionalValidation(
                (type === "intake" || type == "inprogress" || type === "reschedule") ? Yup.string().required("Required Practitioner Preference field.") : null,
                {
                    validation,
                    label: "Practitioner Preference"
                }),
            practitioner: (type === "intake" || type == "inprogress" || type === "reschedule") ?  Yup.mixed()?.when(["practitionerPreference"], {
                is: (practitionerPreference) => practitionerPreference !== "no preference",
                then: Yup.string().required("Required Practitioner field."),
                otherwise: Yup.mixed().notRequired(),
            }) : null,
        }),
        onSubmit: (values) => {
            values.title = values.title?.trim();

            if (onSubmitOverride) {
                setBtnLoading(true);
                let selectedCategoryValue = values?.serviceCategory;
                const selectedServiceCategory = serviceCategory?.find(
                    (categoryData) => categoryData.value === selectedCategoryValue
                );
                const practitionerRecord = practitionerList?.find(
                    (p) => p.id === values?.practitioner
                );

                if (values?.practitionerPreference === "no preference") {
                    values.practitioner = values?.availableSlot?.actorId;
                    values.scheduleId = values?.availableSlot?.scheduleId;
                } else {
                    values.scheduleId = values?.availableSlot?.scheduleId;
                }

                Promise.resolve(
                    onSubmitOverride({
                        title: values.title,
                        date: values.date,
                        serviceCategory: selectedServiceCategory,
                        availableSlot: values.availableSlot,
                        preferredChannel: values.preferredChannel,
                        practitioner: practitionerRecord || values.practitioner,
                        returnTelephoneNumber: values.returnTelephoneNumber,
                        returnTelephoneNumberExt: values.returnTelephoneNumberExt,
                        practitionerPreference: values.practitionerPreference,
                        scheduleId: values.scheduleId,
                    })
                )
                    .catch((res) =>
                        failed(
                            res?.response?.data?.message ||
                                res?.response?.data?.error ||
                                res?.message ||
                                "Failed to submit appointment details"
                        )
                    )
                    .finally(() => setBtnLoading(false));
                return;
            }

            setBtnLoading(true);
            let slot = null;
            let utcStart = values?.availableSlot?.start;
            let utcEnd = values?.availableSlot?.end;
            let reserved = values?.availableSlot?.status == "busy-reserved"
            let selectedServiceCategory = values?.serviceCategory;
            
            selectedServiceCategory = serviceCategory?.filter((categoryData) => categoryData.value === selectedServiceCategory)?.[0]
            if(reserved) {
                selectedServiceCategory = structuredClone(selectedServiceCategory);
                selectedServiceCategory.id = selectedServiceCategory?.value?.split("-")?.[0];
                selectedServiceCategory.name = selectedServiceCategory.name.trim();
            }           
            
            let serviceCategoryPayload = [
                {
                  coding: [
                    {
                      system: "http://florizel.com/AppointmentServiceCategory",
                      code: selectedServiceCategory?.id,
                      display: selectedServiceCategory?.display ? selectedServiceCategory?.display : selectedServiceCategory?.name
                    }
                  ],
                  text: `Appointment for ${selectedServiceCategory?.display ? selectedServiceCategory?.display : selectedServiceCategory?.name}`
                }
            ]

            if(values?.practitionerPreference === "no preference") {
                values.practitioner = values?.availableSlot?.actorId;
                values.scheduleId = values?.availableSlot?.scheduleId;
            }
            else 
                values.scheduleId = values?.availableSlot?.scheduleId;
            
            const requestedPeriod = [{start: utcStart, end: utcEnd}];
            
            //let intakeQuestions = {}
            let patientLocation = LOCATIONS[getValueQuestionnaireDropDown("LOCATION")] || encounterData?.patientLocation || scopeOfPractice;

        
            intakeQuestions["callerNumber"] = values?.returnTelephoneNumber
            intakeQuestions["callerNumberExt"] = values?.returnTelephoneNumberExt
            intakeQuestions["preferredChannel"] = values?.preferredChannel
            intakeQuestions["callerName"] = values?.patient?.replace(/\b\w/g, (c) => c.toUpperCase());
            intakeQuestions["intakeReason"] = values?.title;
            
            if(type == "reschedule") {
                let keepOldSlot = false;
                getSlotById(appointmentData?.slotId)
                .then((response) =>{
                    let data = response?.data?.[0]
                    rescheduleAppointment({
                        serviceCategory: serviceCategoryPayload, 
                        intakeQuestions, 
                        keepOldSlot: false, 
                        slot,  
                        appointmentId,  
                        requestedPeriod, 
                        practitionerId:  values?.practitioner, 
                        scheduleId: values?.scheduleId, 
                        description: values?.title 
                    })
                    .then((res) => {
                        success("Appointment Rescheduled");
                        if(keepOldSlot){
                            updateScheduleSlot({
                                slotId : data?.id,
                                start: data?.start,
                                end: data?.end,
                                status: "busy-reserved",
                                serviceCategories: data?.serviceCategory
                            })
                            .catch(()=>{})
                        }
                        handleShow();
                        handleHide();

                    })
                    .catch((res) => failed(res?.response?.data?.message || res?.response?.data?.error || res.message))
                    .finally(() => setBtnLoading(false));
                })
                .catch(() => {})
            }
            else {
                let topic = buildSessionTopic(user?.name[0]?.text);
                let password= generatePass(); 
                getSignature({ topic: topic.trim(), role: 0, password: password })
                    .then(
                        async (res) => {
                        const sessionToken = res?.data?.sessionToken;
                        if(type == "inprogress") {
                            intakeQuestions["callerName"] = values?.patient?.replace(/\b\w/g, (c) => c.toUpperCase());
                            intakeQuestions["intakeReason"] = values?.title;
                            let reasonQuestion = getTaggedQuestion("REASON");
                            if(reasonQuestion && reasonQuestion?.answer)
                                reasonQuestion.answer = [{valueString: values?.title, option: null}];;

                            intakeQuestions["location"] = patientLocation;
                            delete intakeQuestions.encounter;
                            delete intakeQuestions.encounterId;
                            delete intakeQuestions.id;
                        }
                        bookAppointment({
                                serviceCategory: serviceCategoryPayload, 
                                requestType: (formik?.values?.practitionerPreference == "no preference") ? "organization" : "provider", 
                                slot, 
                                sessionToken, 
                                practitionerId: values?.practitioner, 
                                patientId: patientId, 
                                requestedPeriod, 
                                description: values?.title, 
                                scheduleId: values?.scheduleId, 
                                intakeQuestions, 
                                patientLocation: patientLocation, 
                                newEncounterIdFromModal: (type == "intake") ? encounterId : null,
                                orgId: userOrgId
                            })
                            .then((res) => {
                                if (type === "intake" || type == "inprogress") {
                                    localStorage.removeItem("firstName")
                                    localStorage.removeItem("lastName")
                                    
                                    let updatePayload = {
                                        isHostJoined: false, 
                                        sessionToken, 
                                        id: res?.result?.id, 
                                        status: "booked", 
                                        patientId: res?.result?.patientId, 
                                        practitionerId: res?.result?.practitionerId, 
                                        proceedWith: values.howToProcced || "SCHEDULE", 
                                        intakeQuestions, 
                                        patientLocation, 
                                        newEncounterId: (type == "intake") ? encounterId : (res?.result?.encounterId || null),
                                        orgId: userOrgId
                                    }
                                    updateAppointmentRequest(updatePayload)
                                        .then((res) => {
                                            if(res.status === true){
                                                if(type == "intake") {
                                                    editQuestionnaireResponse(questionnaireResponseId, {encounterId: res?.result?.encounterId, authorName: user?.name[0]?.text, authorType: "Practitioner", authorId: user["custom:practitioner_id"] })
                                                        .then((res)=>{
                                                            handleHide()
                                                        })
                                                        .catch((res) => failed(res?.response?.data?.message || res?.response?.data?.error || res.message))
                                                        .finally(() => setBtnLoading(false));
                                                }
                                                else if(type == "inprogress") {
                                                    // createQuestionnaireResponse({
                                                    //     encounterId: res?.result?.encounterId, 
                                                    //     appointmentId: res?.result?.id,
                                                    //     patientID: patientId,
                                                    //     orgId: userOrgId,
                                                    //     authorName: user?.name[0]?.text, 
                                                    //     authorType: "Practitioner", 
                                                    //     authorId: user["custom:practitioner_id"] ,
                                                    //     questionResponse: intakeQuestions.item.map(({text: question, ...rest}) => ({question, ...rest}))
                                                    // })
                                                    //    .then((res)=>{
                                                            handleHide()
                                                    //   })
                                                    //    .catch((res) => failed(res?.response?.data?.message || res?.response?.data?.error || res.message))
                                                    //    .finally(() => setBtnLoading(false));
                                                }
                                            }
                                        })
                                        .catch((err) => failed(err?.response?.data?.message || err?.response?.data?.error ||  err?.message))
                                        .finally(() => setBtnLoading(false));
                                }
                                success("Appointment Scheduled");
                                handleHide(true);
                            })
                            .catch((res) => failed( res?.response?.data?.message || res?.response?.data?.error || res.message))
                            .finally(() => setBtnLoading(false));                     
                        }
                    )
                    .catch((error) => {
                        failed(
                        error?.response?.data?.message ||
                            error?.response?.data?.error ||
                            error?.message ||
                            "Something went wrong. Please try again."
                        );
                        setBtnLoading(false);
                    });
            }
        },   
    })

    // functions
    const taggedQuestions = intakeQuestions?.item;
    const isTagPresent = (tagValue) => intakeQuestions?.item?.some(item => item.tag === tagValue);
    const getTaggedQuestion = (tagValue) => taggedQuestions?.find(item => item.tag === tagValue)
    const getValueQuestionnaire = (tagValue) => taggedQuestions?.find(item => item.tag === tagValue)?.answer?.[0]?.answer
    const getValueQuestionnaireDropDown = (tagValue) => {
        let tagged = taggedQuestions?.find((obj)=> obj?.tag === tagValue);
        if(tagged) {
            let answer = tagged.answer?.find((val)=> (val?.answer !== "" || val?.valueString !== ""))
            return (answer?.answer || answer?.valueString)
        }
        return null;
    }
    
    const getValueQuestionnaireExt = (tagValue) => taggedQuestions?.find(item => item.tag === tagValue)?.answer?.[0]?.answer?.split("ext ")?.[1]
    
    const onSuccess = (data) => {
        setPatientsList(data?.map((org) => (org = { value: `{"id": "${org?.id}", "location": "${org?.address[0]?.state}"}`, name: org?.name?.[0]?.text?.split(' ')?.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '), id: org.id })));
    };
    const { isLoading, } = usePatientsBySOPQuery({ onSuccess, practitioner_id: practitionerId, org_id: effectiveOrgId})

    /**
     * gets the encounter details based on provided
     * encounterId and stores it state, in addition,
     * extracts the appointmentId if any, and patientId
     */
    const getEncounterDetails = (encounterId) => {
        if(encounterId) {
            getEncounterRecord({encounterId }).then(res => {
                // console.log("Res", JSON.stringify(res))
                let encData = res?.data;
                setEncounterData(encData);
                setAppointmentId(encData?.appointmentId || null)
                setPatientId(encData?.patientId || patient);
                if(!encData?.appointmentId) {
                    let qrId = qRId || encData?.intakeQuestions?.questionnaireResponseId?.reference?.split("/")?.pop() || encData?.intakeQuestions?.id ;
                    getIntakeQuestions(qrId)
                    formik.setFieldValue("title", isTagPresent("REASON") ?  getValueQuestionnaire("REASON") : "")
                }
            })
            .catch(err => {
            return; 
            })
        }
    }

    /** 
     * gets the details of the provided appointment
     * if any, stores the details in state
    */
    const getAppointmentDetails = (appointmentId) => {
        if(appointmentId) (
            getAppointmentRecord({appointmentId}).then(res => {
                setAppointmentData(res.data);
                if(qRId)
                    getIntakeQuestions(qRId)               
                formik.setFieldValue("title", res.data.description || res.data.intakeQuestions["intakeReason"] || "");
                formik.setFieldValue("returnTelephoneNumber", res.data.intakeQuestions["callerNumber"] || "");
                formik.setFieldValue("returnTelephoneNumberExt", res.data.intakeQuestions["callerNumberExt"] || "");
            })
            .catch(err => {
                return;
            })
        )
    }

    /**
     * gets the intake questionnaire for the encounter/appointment
     * if any, and stores the values in state.
     */
    const getIntakeQuestions = (questionnaireResponseId) => {
        if(questionnaireResponseId) {
            getQuestionnaireResponseById(questionnaireResponseId).then(res => {
                // console.log("Res", JSON.stringify(res))
                setIntakeQuestions(res.data);
                if(appointmentData) {
                    formik.setFieldValue("returnTelephoneNumber", appointmentData?.intakeQuestions?.callerNumber || res.data?.item?.find(item => item.tag === "TELEPHONE")?.answer?.[0]?.valueString)
                    formik.setFieldValue("title", appointmentData?.intakeQuestions?.intakeReason || res.data?.item?.find(item => item.tag === "REASON")?.answer?.[0]?.valueString)      
                }
                else {
                    formik.setFieldValue("returnTelephoneNumber", res.data?.item?.find(item => item.tag === "TELEPHONE")?.answer?.[0]?.valueString)
                    formik.setFieldValue("title", res.data?.item?.find(item => item.tag === "REASON")?.answer?.[0]?.valueString)      
                }
            })
            .catch(err => {
            return; 
            })
        }
    }

    /**
     * gets the patient details based on provided patientId
     * and if there is one, stores the data in state
     */
    const getPatientDetails = (patientId) => {
        if(patientId) {
            getPatientData(patientId).then(res =>{
                // console.log("Res", JSON.stringify(res));
                setPatientData(res.result);
                formik.setFieldValue("patient", res.result.name?.[0]?.text)  
            })
            .catch(err => {
                return;
            })
        }
    }

    /**
     * event handler on change to the practitioner dropdown,
     * This will reload/reset all slots and service categories
     */
    const handlePractitionerChange = (event) => {
        setPractitionerId(event?.target?.value)
        formik.setFieldValue("practitioner", formik?.values?.practitionerPreference === "no preference" ? "" : event?.target?.value);
        formik.setFieldValue("serviceCategory", ""); 
        formik.setFieldValue("date", "");
        setServiceCategory([]);
        setAvailableSlots([]);
        setCategoriesByDay([]);
    };

    /** 
     * works in tandem with the handlePractitionerChange.
     * Resets slots and service categories.
     */
    const handlePractitionerPreferenceChange = (e) => {
        setPractitionerId(null); 
        formik.setFieldValue("practitioner", "");
        formik.setFieldValue("serviceCategory", ""); 
        formik.setFieldValue("date", "");
        setServiceCategory([]);
        setAvailableSlots([]);        
        setCategoriesByDay([]);
        formik.setFieldValue("practitionerPreference", e.target.id);
    };

    /**
     * event handler on change to the scheduling month dropdown.
     * Resets slots and service categories so they reload for the selected month.
     */
    const handleSchedulingMonthChange = (event) => {
        setPractitionerId(null); 
        formik.setFieldValue("schedulingMonth", event?.target?.value);
        formik.setFieldValue("practitioner", "");
        formik.setFieldValue("serviceCategory", "");
        formik.setFieldValue("date", "");
        setServiceCategory([]);
        setAvailableSlots([]);
        setCategoriesByDay([]);
    };
    
    /**
     * in the case where a patient dropdown is used (not currently supported but here
     * for purposes of possible extension) updates the patient and scope of practice based
     * on the patient
     */
    const handlePatientChange = (event) => {
        formik.setFieldValue("patient", event?.target?.value);
        setScopeOfPractice(JSON.parse(event?.target?.value)?.location); 
    };

    /***
     * merge is used to merge 2 objects.  Previously used Object.assign and it would
     * cause data lose, so this is a better method for the categories by day which
     * modifies the obj1 with data from obj2.
     */
    const merge = (obj1, obj2) => {
        for(let key in obj2) {
            if(obj1[key]) {
                // if both elements contain an array, then extend the array
                if(Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
                    obj2[key].forEach(cat => {
                        if(!obj1[key].includes[cat])
                            obj1[key].push(cat);
                    })
                }
                else {
                    // else replace the value with matching obj2
                    obj1[key] = obj2[key]
                }
            }
            else 
                obj1[key] = obj2[key];
        }
        return obj1;
    }

    /**
     * removeLateSlots
     * params: 
     * slots (array of slot objects)
     * date (date of event)
     * This logic will remove any slots that occur after midnight local time
     * when attempting to pick a slot on a day         
     */
    const removeLateSlots = (slots, date) => {
        let midnight = date || new Date();
        // console.log("DATE", midnight) // date before processing
        midnight = new Date(midnight.setDate(midnight.getDate()+1));
        midnight.setHours(0)
        midnight.setMinutes(0)
        midnight.setSeconds(0);
        // console.log("REMOVE LATE SLOTS MIDNIGHT: ", midnight); // midnight of submitted date.
        if(Array.isArray(slots)) {
            // console.log("slot count", slots.length)
            const filteredSlots = slots.filter(slot => {
                // console.log("SLOT END", slot.end)
                let endTime = new Date(slot.end).getTime();
                let midnightTime = midnight.getTime();
                //console.log(endTime < midnightTime, slot.start)
                return (endTime < midnightTime)
            })
            //console.log("slot count after filter", filteredSlots.length)
            //setOrgSlotsData(filteredSlots);
            return filteredSlots;
        }
        else return slots;        
    }

    /**
     * removeSlotsOutsideOfDayRange 
     * params:
     *  slots: array of slot objects
     *  date: date to filter.
     * 
     * Removes slots that occur before midnight local time.
     * Works in tandem with removeLateSlots to only render slots for the 24
     * hr period of the user's local time.
     */
    const removeSlotsOutsideOfDayRange = (slots, date) => {
        let midnight = date || new Date();
        // midnight = new Date(midnight.setDate(midnight.getDate()));
        midnight.setHours(0)
        midnight.setMinutes(0)
        midnight.setSeconds(0);
        // console.log("REMOVE OUTSIDE DAY RANGE MIDNIGHT: ", midnight); // start of submitted day, localtime.
        if(Array.isArray(slots)) {
            // console.log("slot count", slots.length)
            const filteredSlots = slots.filter(slot => {
                // console.log("SLOT START", slot.start)
                let startTime = new Date(slot.start).getTime();
                let midnightTime = midnight.getTime();
                // console.log(startTime > midnightTime, slot.start)
                return (startTime > midnightTime)
            })
            // console.log("slot count after filter", filteredSlots.length)
            //setOrgSlotsData(filteredSlots);
            return filteredSlots;
        }
        else return slots;
    }

    /**
     * handleDateSelect
     * params:
     *  val: date
     * Resets and re-retrieves slots for a date.
     */
    const handleDateSelection = (val) => {
        const isValidDate = moment(val, 'MMM-DD-YYYY').isValid()
        setAvailableSlots([])
        if(type === "intake" || type === "inprogress"){
            if(formik.values.practitionerPreference == ''){
                failed('Please select practitioner preference')
            }else{
                if(isValidDate){
                    formik.setFieldValue("date", val.format("YYYY-MM-DD"));
                    // load slots here
                    loadSlotData(val.format("YYYY-MM-DD"))
                }else{
                    formik.setFieldError('date', 'Invalid date format')
                }
            }
        }
        else {
            if(isValidDate){
                formik.setFieldValue("date", val.format("YYYY-MM-DD"));
                loadSlotData(val.format("YYYY-MM-DD"))
            }else{
                formik.setFieldError('date', 'Invalid date format')
            }
        }       
    }

    /**
     * highLightDates
     * utility function to set highlights on dates in a calendar date picker when there
     * are available slots.
     */
    const highLightDates = () => {
        setAvailableDays([]);
        const today = moment();
        const now = moment();
        const toHighlight = [];

        const foundCategory = serviceCategory.find(
            (item) => item.id === formik.values.serviceCategory
        );

        if (!foundCategory || !foundCategory.timeSlot) {
            setAvailableDays([]);
            return;
        }

        const timeSlot = parseInt(foundCategory.timeSlot.value, 10);
        const availabilities = computedServiceCategories?.[formik.values.serviceCategory] || [];

        availabilities.forEach((period) => {
            let periodStart = moment(period.start);
            let periodEnd = moment(period.end);

            // Only consider today or future days
            if (periodStart.isBefore(today, "day")) return;

            // Adjust current period if it’s today
            if (periodStart.isSame(today, "day")) {
                if (periodEnd.isBefore(now)) 
                    return;
                if (periodStart.isBefore(now)) 
                    periodStart = now.clone();
            }

            const availableMinutes = periodEnd.diff(periodStart, "minutes");
            if (availableMinutes >= timeSlot) {
                const dayKey = periodStart.format("YYYY-MM-DD");
                if (!toHighlight.includes(dayKey)) {
                    toHighlight.push(dayKey);
                }
            }
        });

        setAvailableDays(toHighlight);
    };

    /** fetchSchedules 
     * finds all active schedules with active service categories
     */
    const fetchSchedules = async (schedules, { isCancelled } = {}) => {
        const schedulesWithSlots = [];
        const scheduleList = schedules || [];

        for (let i = 0; i < scheduleList.length; i += SCHEDULE_SLOTS_BATCH_SIZE) {
            if (isCancelled?.()) return;
            if (i > 0) {
                await new Promise((resolve) => setTimeout(resolve, SCHEDULE_SLOTS_BATCH_DELAY_MS));
            }
            if (isCancelled?.()) return;

            const batch = scheduleList.slice(i, i + SCHEDULE_SLOTS_BATCH_SIZE);
            const batchResults = await Promise.allSettled(
                batch.map(async (schedule) => {
                    const slotsResponse = await getScheduleSlots(schedule.id);
                    const slots = slotsResponse.data || [];
                    return { ...schedule, slots };
                })
            );

            batchResults.forEach((result) => {
                if (result.status === "fulfilled") {
                    schedulesWithSlots.push(result.value);
                }
            });
        }

        if (isCancelled?.()) return;
        const result = computeServiceCategoryAvailabilities(schedulesWithSlots);
        setComputedServiceCategories(result);
    };

    /** removes any slot that has a start time before this instant */
    const removePastSlots = (slots) => {
        let now = new Date().getTime();
        return slots?.filter(slot => new Date(slot.start).getTime() > now);
    }

    /** sorts slots based on time */
    const sortedSlots = (data) => {      
        return data?.sort((a,b) => {
            let atime = (moment(a.start).isValid()) ? `${a.start}`:`${moment(formik.values.date)?.format("YYYY-MM-DD")}T${a?.start}:00Z`;
            let btime = (moment(b.start).isValid()) ? `${b.start}`:`${moment(formik.values.date)?.format("YYYY-MM-DD")}T${b?.start}:00Z`;
            return (new Date(atime).getTime() < new Date(btime).getTime()) ? -1 : 1;            
        })
    }
    /** sorts any object based on a name attribute */
    const sortedObj = data => {
        return data.sort((a,b) => {
            return a.name > b.name ? 1 : -1
        });
    }

    const findUpcoming = (event) => {
        setSlotUnavailable(false);        
        let targetVal = event?.target?.value;
        formik.setFieldValue("serviceCategory", targetVal);
        setAvailableSlots([])
        formik.setFieldValue("date", "");
    }

    const renderDay = (props) => {
        const { selectedDate, day, dayComponent, ...other} = props;
        const today = moment();
        const isAvailable = availableDays.includes(day.format("YYYY-MM-DD"));
        const isSelected = day.isSame(selectedDate, "day");
        const isToday = day.isSame(today, "day");
        const isPast = day.isBefore(today, "day")

        return (
            <PickersDay {...dayComponent} 
                disabled={isPast || !isAvailable}
                style={isAvailable ? {background: "#accfeb"}: {background: "white" }} />
        );
    }

    /**
     * Loads available slots for a given date and appointment type.
     * Handles reserved slots, merges with available slots, and sorts them.
     */
    const loadSlotData = (selectedDate, selectedType) => {
        setSlotsLoading(true);
        setSlotUnavailable(false);

        const today = moment();
        const date = moment(selectedDate || formik?.values?.date);
        const type = selectedType || formik?.values?.serviceCategory;
        const dateUtc = date.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";

        if (!type) {
            setSlotsLoading(false);
            return;
        }

        // Get reserved slots for this type
        let reservedSlots = reservedCategories[type] || [];
        reservedSlots = reservedSlots
            .filter(slot => moment(slot.start).isAfter(date) && moment(slot.start).isSame(date, "day"));
        reservedSlots = sortedSlots(reservedSlots);

        // Prepare params for fetching available slots
        const params = {
            actorId: practitionerId || modalShow?.id || user["custom:unique_id"],
            date: dateUtc,
            serviceCategoryId: type,
            orgId: formik?.values?.practitionerPreference === "no preference"
                ? effectiveOrgId
                : effectiveOrgId
        };

        // Add scopeOfPractice if needed
        if (formik?.values?.practitionerPreference === "no preference") {
            delete params.actorId;
            if (scopeOfPractice) {
                params.scopeOfPractice = scopeOfPractice;
            } else if (taggedQuestions) {
                const locationTag = taggedQuestions.find(el => el.tag === "LOCATION");
                if (locationTag) {
                    const selected = locationTag?.answer?.find(val => val?.["valueString" || "answer"]?.length > 0);
                    if (selected) {
                        const tempScope = LOCATIONS[(selected?.valueString || selected?.answer)];
                        setScopeOfPractice(tempScope);
                        params.scopeOfPractice = tempScope;
                    }
                }
            }
        }

        getSlots(params)
            .then(res => {
                let available = res?.data?.availableSlots || [];
                // Remove past/late slots
                if (date.isSame(today, "day")) {
                    available = removeLateSlots(removePastSlots(available), new Date(dateUtc));
                } else {
                    available = removeLateSlots(removeSlotsOutsideOfDayRange(available, new Date(dateUtc)), new Date(dateUtc));
                }
                available = sortedSlots(available);

                // Merge reserved slots, avoiding duplicates
                reservedSlots.forEach(slot => {
                    if (!available.some(item => item.start === slot.start)) {
                        available.push(slot);
                    }
                });

                available = sortedSlots(available);
                setAvailableSlots(available);

                if (available.length < 1) {
                    setSlotUnavailable(true);
                }
            })
            .catch(err => {
                if (reservedSlots.length > 0) {
                    setAvailableSlots(reservedSlots);
                } else {
                    failed(err?.response?.data?.message || err?.response?.data?.error || err?.message);
                    setAvailableSlots([]);
                }
            })
            .finally(() => setSlotsLoading(false));
    };

    useEffect(() => {
        getEncounterDetails(encounterId);
    }, [encounterId])

    useEffect(() => {
        getAppointmentDetails(appointmentId);
    }, [appointmentId]);

    // useEffect(() => {
    //     let qrId = qRId || encounterData?.intakeQuestions?.questionnaireResponseId?.reference?.split("/")?.pop() || encounterData?.intakeQuestions?.id ;
    //     setQRId(qrId);
    //     getIntakeQuestions(qrId)
    // }, [encounterData])

    useEffect(() => {
        getPatientDetails(patientId)
    }, [patientId])

    useEffect(() => {
        if (prefillTitle) {
            formik.setFieldValue("title", prefillTitle);
        }
    }, [prefillTitle]);

    useEffect(() => {
        const scope = (taggedQuestions && Array.isArray(taggedQuestions) && taggedQuestions.length > 0) ? LOCATIONS[taggedQuestions?.filter(el => el.tag === "LOCATION")?.[0]?.answer?.find((val) => val?.valueString?.length > 0)?.option] : (encounterData?.intakeQuestions?.location); // || encounterData?.intakeQuestions["Patient Location"]);

        getlistPractitioners({orgId: effectiveOrgId , scopeOfPractice: scope })
          .then(async (res) => {
            let allPracts = res.result?.filter(pra => pra.active);
            let activePractitioners = [];
            let today = moment();
            let startDate = moment(formik?.values?.schedulingMonth).startOf("month");
            
            if(startDate.isBefore(today))
                startDate = today;

            startDate = moment(startDate).utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
            
            await checkSchedule({actorId: user["custom:unique_id"], date: startDate, orgId: effectiveOrgId, endDate: moment(formik?.values?.schedulingMonth).endOf("month").utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"})
            .then((activeSchedules)=> {
                if(!activeSchedules?.data) { 
                    // no active schedules, so list no practitioners
                    failed(activeSchedules?.message);
                    setServiceCategory([]);
                    setCategoriesByDay({});
                    setPractitionerList([]);
                    return;
                }
                if(!Array.isArray(activeSchedules.data)) 
                    activeSchedules.data = [activeSchedules.data];

                // remove empty schedules
                activeSchedules.data = activeSchedules.data.filter(async sch => {
                    let partOfOrg = scheduleMatchesSelectedOrg(sch, effectiveOrgId);
                    let hasCategories = sch.serviceCategory?.length > 0 && sch.serviceCategory.filter(cat => moment(cat.date).isSameOrAfter(moment(startDate), "day")).length > 0

                    // definite possibility of slots here, return true
                    if(partOfOrg && hasCategories) return true;
                    else if(partOfOrg) {
                        // check if the schedule has any reserved categories if there are no active service categories.
                        let slotData = await getScheduleSlots(sch.id, "busy-reserved", effectiveOrgId);
                        return slotData.data.length > 0
                    }
                    else return false;
                })
                
                activeSchedules.data.forEach(schedule => {
                    let active = allPracts.find(person => person.id === schedule.actorId && !activePractitioners.some(exists => exists.id === schedule.actorId))
                    if(active) 
                        activePractitioners.push(active);
                })
                // practitioners that have active schedules for the current month.
                setPractitionerList(activePractitioners.map(ele => (
                    { 
                        value: ele?.id, 
                        name: ele?.name?.[0]?.text?.split(' ')?.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '), // capitalize all names, surname, given name, etc.
                        id: ele?.id 
                    }
                ))) 
            })                
            .catch((activeSchedules) => {
                failed(activeSchedules?.response?.data?.message || activeSchedules?.response?.data?.error || activeSchedules.message);
                setServiceCategory([]);
                setCategoriesByDay({});
                setPractitionerList([]);
            });            
          })
          .catch((res) => {
            console.log("error fetching practioner list",res)
          })
    }, [encounterData, appointmentData, intakeQuestions, formik?.values?.schedulingMonth]);

    useEffect(() => {
        if(type === "intake" || type === "inprogress" || type === "reschedule"){
            if(type == "intake")
                formik.setFieldValue("title", isTagPresent("REASON") ?  getValueQuestionnaire("REASON") : "");
            
            formik.setFieldValue("patient", encounterData?.subject?.display);
            formik.setFieldValue("returnTelephoneNumber", isTagPresent("TELEPHONE") ?  taggedQuestions?.find(item => item.tag === "TELEPHONE")?.answer?.[0]?.answer?.split(" ext")?.[0] : "");
            formik.setFieldValue("returnTelephoneNumberExt" , isTagPresent("TELEPHONE")? getValueQuestionnaireExt("TELEPHONE") : "");
        }

        return () => {
            let nameAnswer;
            const nameQuestion = taggedQuestions?.find(question => question.tag === "CALLER");
            if (nameQuestion) {
                nameAnswer = nameQuestion?.answer;
                if(Array.isArray(nameAnswer)) 
                    nameAnswer[0].answer = localStorage.getItem("firstName")
                if(nameQuestion?.answer?.[1] === undefined) 
                    nameAnswer.push({answer: localStorage.getItem("lastName")}) 
                nameAnswer[0].required = true;
                nameAnswer[0].option = null;
                nameAnswer[0].value = null;
                nameAnswer[0].name = null;
            }
            //formikInbound?.setFieldValue("questionResponse", encounterData.intakeQuestions)
        }
    }, []);

    // refresh UI data on change of 
    useEffect(() => {
        let cancelled = false
        let today = moment();
        let start = moment(formik?.values?.schedulingMonth).startOf("month");
        
        let startISO = start.isBefore(today) ? today.toDate().toISOString()?.split(".")?.[0]+"Z" : start.toDate().toISOString()?.split(".")?.[0]+"Z";
        let endISO = moment(formik?.values?.schedulingMonth).endOf("month").utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
        let selectedDay = moment(formik?.values?.date).toDate();
        let todayISO = today.toDate().toISOString()?.split(".")?.[0]+"Z";
        
        let noPreference = formik?.values?.practitionerPreference === "no preference";

        const applyReservedSlotFilters = (slotsByCategory) => {
            if (Array.isArray(slotsByCategory)) {
                if (moment(startISO).isSame(moment(startISO), "day")) {
                    return removePastSlots(slotsByCategory);
                }
                return removeSlotsOutsideOfDayRange(
                    removePastSlots(slotsByCategory),
                    selectedDay
              );
            }
      
            Object.keys(slotsByCategory).forEach((key) => {
                if (moment(startISO).isSame(moment(startISO), "day")) {
                    slotsByCategory[key] = removePastSlots(slotsByCategory[key]);
                } 
                else {
                    slotsByCategory[key] = removeSlotsOutsideOfDayRange(
                        removePastSlots(slotsByCategory[key]),
                        selectedDay
                    );
                }
            });
            return slotsByCategory;
        };
      
        const appendReservedCategories = (categories, reservedSlots) => {
            let reservedByDay = {};
      
            Object.keys(reservedSlots).forEach((key) => {
                if (
                    !reservedSlots[key]?.length ||
                    !(reservedSlots[key][0]?.serviceCategory?.[0]?.id || reservedSlots[key][0]?.serviceCategory?.[0]?.coding?.[0]?.code)
                ) return;
                
                if (
                    !moment(reservedSlots[key][0]?.start).isSameOrAfter(startISO, "day")
                ) return;
                
                const categoryLabel =
                    reservedSlots[key]?.[0]?.serviceCategory[0]?.name ||
                    reservedSlots[key]?.[0]?.serviceCategory[0]?.coding?.[0]?.display;
                
                const categoryId =
                    reservedSlots[key][0]?.serviceCategory?.[0]?.id ||
                    reservedSlots[key][0]?.serviceCategory?.[0]?.coding?.[0]?.code;
              
                const alreadyListed = categories.some((item) => item.value === categoryId);
      
                if (!alreadyListed) {
                    let timeSlot = reservedSlots[key][0]?.serviceCategory?.[0]?.timeSlot;
                    if (!timeSlot) {
                        const timeSlotString = reservedSlots[key]?.[0]?.serviceCategory[0]?.coding?.[0]?.display
                            ?.split("(")[1]
                            ?.split(")")[0]
                            ?.split(" ");
                        if (timeSlotString?.length) {
                            timeSlot = {
                                value: parseInt(timeSlotString[0], 10),
                                unit: "minutes",
                            };
                        }
                    }
      
                    // const name = `${
                    //     formik.values.practitioner?.length > 0 ? "Reserved for " : ""
                    // } ${categoryLabel}`;
      
                    categories.push({
                        name: categoryLabel,
                        id: categoryId,
                        value: categoryId,
                        timeSlot,
                        orgId: reservedSlots[key][0].orgId,
                        date: reservedSlots[key][0]?.start,
                        status: "busy-reserved",
                    });
                }
      
                reservedSlots[key].forEach((slot) => {
                    const datekey = moment(slot?.start).format("YYYY-MM-DD");
                    const startTimestamp = moment(slot?.start).valueOf();
                    
                    if (moment(startTimestamp).isSameOrAfter(moment(startISO), "day")) {
                        if (reservedByDay[datekey] && !reservedByDay[datekey].includes(categoryId)) {
                            reservedByDay[datekey].push(categoryId);
                        } else {
                            reservedByDay[datekey] = [categoryId];
                        }
                    }
                });
            });
      
            if (Object.keys(reservedByDay).length > 0) {
              setCategoriesByDay((prev) => merge({ ...prev }, reservedByDay));
            }
        };

        // verify practitioner preference settings exist.
        if(formik?.values?.practitionerPreference &&
            ((!noPreference && practitionerId) || noPreference)) {
            
            setSchedulesLoading(true);
            checkSchedule({ 
                actorId: (noPreference ? user["custom:unique_id"] :  practitionerId), 
                date: startISO, 
                orgId: noPreference ? effectiveOrgId : null,
                endDate: endISO
            })
            .then(async res => {
                    
                if (cancelled) return;
                        
                if(!res?.data) {
                    failed(res?.message);
                    setServiceCategory([]);
                    setCategoriesByDay({});
                    return;
                }
                
                let categories = [];
                if(!Array.isArray(res.data)) 
                    res.data = [res.data]; // for the schedule results into an array

                // remove any schedules from different organizations than the current user's 
                // this is just a safety/sanity check as it should fail if the practitioner doesn't 
                // belong to the same organization as the selected practitioner, or if no preference
                // it should only return valid schedules.
                let availableSchedules = res.data.filter(sch => scheduleMatchesSelectedOrg(sch, effectiveOrgId))
                let tempByDay = {};

                availableSchedules.forEach(schedule => {

                    // find all categories available for each day
                    schedule.serviceCategory.forEach(category => {
                        if(category.id) { // occasional bad data where there is a date and no id, so do not process
                            let categoryDate = moment(category?.date);
                            let key = categoryDate.format("YYYY-MM-DD");
                            if(moment(category?.date).isSameOrAfter(startISO, "day") && moment(category?.date).isSameOrBefore(endISO)) {
                                if(!categories.some(item => item.value === category.id)) 
                                    categories.push({...category, value: category?.id}); 
                                    
                                if(tempByDay[key]) {
                                    if(!tempByDay[key].some(id => id === category.id )) 
                                        tempByDay[key].push(category.id)
                                }
                                else tempByDay[key] = [category.id];   
                            }
                        }                              
                    })
                })

                setCategoriesByDay(merge(categoriesByDay, tempByDay)) 
                let reservedSlots ={}

                // Batch getScheduleSlots to avoid unbounded parallel fan-out (FLORIZEL-4053)
                const reservedResponses = [];
                for (let i = 0; i < availableSchedules.length; i += SCHEDULE_SLOTS_BATCH_SIZE) {
                    if (cancelled) return;
                    if (i > 0) {
                        await new Promise((resolve) => setTimeout(resolve, SCHEDULE_SLOTS_BATCH_DELAY_MS));
                    }
                    if (cancelled) return;

                    const batch = availableSchedules.slice(i, i + SCHEDULE_SLOTS_BATCH_SIZE);
                    const batchResults = await Promise.allSettled(
                        batch.map((schedule) =>
                            getScheduleSlots(schedule.id, "busy-reserved", effectiveOrgId)
                                .then((resp) => ({ schedule, resp }))
                        )
                    );
                    reservedResponses.push(...batchResults);
                }
                            
                if(cancelled) return;
                        
                reservedResponses.forEach((result) => {
                    // Soft-fail individual schedule slot errors so one failure does not toast-spam
                    // or block remaining successful schedules from populating Appointment Type.
                    if (result.status === "rejected") {
                        return;
                    }
                    
                    const { schedule, resp } = result.value;
                    resp.data
                        ?.filter((slot) =>
                            moment(slot?.start).isSameOrAfter(moment(startISO), "day")
                        )
                        ?.forEach((slot) => {
                            slot.serviceCategory?.forEach((cat) => {
                                const serviceCategoryId = cat.id || cat.coding?.[0]?.code;
                                if (!serviceCategoryId) 
                                    return;
                
                                const slotEntry = {
                                    start: slot?.start,
                                    end: slot?.end,
                                    scheduleId: slot?.scheduleId,
                                    serviceCategory: [cat],
                                    orgId: slot?.orgId,
                                    actorId: schedule.actorId,
                                    status: "busy-reserved",
                                };
                    
                                if (reservedSlots[serviceCategoryId]) {
                                    reservedSlots[serviceCategoryId].push(slotEntry);
                                } 
                                else {
                                    reservedSlots[serviceCategoryId] = [slotEntry];
                                }
                            });
                        });
                });
                            
                reservedSlots = applyReservedSlotFilters(reservedSlots);
                appendReservedCategories(categories, reservedSlots);
                    
                setReservedCategories(reservedSlots);
                setServiceCategory([...categories]);

                // Run after reserved batches so we never fan out both slot fetches at once.
                await fetchSchedules(availableSchedules, { isCancelled: () => cancelled });
            })
            .catch((res) => {
                if (cancelled) return;
                failed(
                    res?.response?.data?.message ||
                    res?.response?.data?.error ||
                    res.message
                );
                setServiceCategory([]);
                setCategoriesByDay({});
            })
            .finally(() => {
                if (!cancelled) setSchedulesLoading(false);
            });
            
            return() => {
                cancelled = true
                setSchedulesLoading(false);
            }
        }
    },[practitionerId, formik?.values?.practitioner, formik?.values?.practitionerPreference, formik?.values?.schedulingMonth])
    
    return (
        <>
            <Modal
                backdropClassName
                backdrop={"static"}
                show={handleShow}
                onHide={handleHide}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                className="custom-dialog appointment-custom-modal"
            >
                <Modal.Header closeButton className="border-0">
                    <Modal.Title id="contained-modal-title-vcenter">
                        {isInternalReferral ? (
                            "Internal Referral - Book An Appointment"
                        ) : (
                            <>
                                {(type == "reschedule") ? "Reschedule":"Create" } Appointment for{" "}
                                <span style={{ textTransform: "capitalize" }}>{encounterData?.subject?.display || encounterData?.intakeQuestions?.callerName || formik.values.patient || ""}</span>
                            </>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
                    {isLoading ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                            }}
                        >
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            <Row>
                                <Col>
                                    <SelectFiled
                                        keyField={"schedulingMonth"}
                                        label={"Scheduling Month"}
                                        formik={formik}
                                        options={monthOptions}
                                        onChange={handleSchedulingMonthChange}
                                    />
                                </Col>
                                <Col></Col>
                            </Row>
                            <Row>
                                <Col>
                                    <TextInput keyField={"title"} label={effectiveReasonLabel} disabled={(type == "reschedule") ? true : false} formik={formik} placeholder={effectiveReasonLabel} />
                                </Col>
                                {!isInternalReferral ? (
                                    <Col>
                                        {!type ? (<SelectFiled keyField={"patient"} label={"Patient Name"} formik={formik} options={patientsList}  onChange={handlePatientChange} />)
                                        : (<TextInput keyField={"patient"} label={"Patient Name"} formik={formik} disabled={true} placeholder={"Patient Name"} style={{textTransform: "capitalize"}} />)}
                                    </Col>
                                ) : null}
                            </Row>
                            {(type === "intake" || type === "inprogress" || type === "reschedule") ? 
                            <Row>
                                <Col>
                                    <Form.Check.Label style={{ textTransform: "capitalize", fontSize: "14px" }} >{"practitioner preference ?"}</Form.Check.Label> <span className="requiredField">&#42;</span><br/>
                                    {["select practitioner", "no preference"].map((val) => (
                                        <Form.Check style={{ textTransform: "capitalize", fontSize: "14px", marginRight: "45px"}} inline checked={formik.values.practitionerPreference === val} type={"radio"} label={val} id={val} name="practitioner preference"  onChange={handlePractitionerPreferenceChange} />
                                     ))}
                                     {formik?.touched["practitionerPreference"] && formik?.errors["practitionerPreference"] ? (<div className="error-text">{formik?.errors["practitionerPreference"]} </div>) : null}
                                </Col>
                                {formik?.values?.practitionerPreference === "select practitioner" ? (
                                    <Col>
                                        <SelectFiled keyField={"practitioner"} label={"Select Practitioner"} formik={formik} options={sortedObj(practitionerList)} isReadOnly={isReadOnly} onChange={handlePractitionerChange} />
                                    </Col>
                                ) : <Col></Col>}
                                
                            </Row> : null}
                            <Row className="mt-3">
                                <Col>
                                    <SelectFiled keyField={"serviceCategory"} label={"Appointment Type"} formik={formik} options={sortedObj(serviceCategory)} disabled={!formik.values.practitionerPreference || schedulesLoading} onChange={findUpcoming} />
                                    {schedulesLoading ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                                            <CircularProgress size={18} />
                                            <span style={{ fontSize: "14px" }}>Fetching schedules...</span>
                                        </div>
                                    ) : null}
                                </Col>
                                <Col>
                                    <DateSelectorWithoutFormik
                                        key={formik?.values?.schedulingMonth}
                                        type="readonly"
                                        disabled={!formik?.values?.serviceCategory || schedulesLoading}
                                        formik={formik}
                                        keyField={'date'}
                                        label="Date"
                                        value={(formik?.values?.date?.length > 0) ? moment(formik?.values?.date) : null}
                                        handleChange={(value) => { handleDateSelection(value) }}
                                        minDate={moment.max(moment(), moment(formik?.values?.schedulingMonth).startOf("month"))}
                                        maxDate={moment(formik?.values?.schedulingMonth).endOf("month")}
                                        views={["day"]}
                                        defaultCalendarMonth={moment(formik?.values?.schedulingMonth)}
                                        PopperProps={{ sx: { ".MuiPickersArrowSwitcher-root": { display: "none" } } }}
                                        onMonthChange={highLightDates}
                                        onOpen={highLightDates}
                                        renderDay={(day, selectedDate, dayComponent) => renderDay({ day, selectedDate, dayComponent })}
                                    />
                                </Col>
                            </Row>
                            {availableSlots?.length > 0 ?
                                <>
                                    <Row>
                                        <Col>Select Slot</Col>
                                    </Row>
                                    <Row style={{ rowGap: "0.5rem", marginBottom: "1.5rem", marginTop: "0.5rem" }}>
                                        {availableSlots?.map((slot, index) => {
                                        let localTime = moment(slot?.start).format("HH:mm");
                                        
                                        return ( <Col style={{ background: selectedSlot === slot?.start ? "#6c757d38" : "#ffff", width: "14.666667%" }} sm={2} className="slotStyle" key={index} onClick={() => { formik?.setFieldValue("availableSlot", slot); setSelectedSlot(slot?.start) }}>{localTime}</Col>)
                                        })}
                                    </Row>
                                </>
                                : <div className="error-text" style={{marginBottom: "1.5rem", display: slotUnavailable ?  "block" : "none" }}>
                                No Available Slots
                             </div>}
                                
                                {slotsLoading ?
                                <>
                                    <Row>
                                        <Col>Select Slot</Col>
                                    </Row>
                                    <Skeleton height={50} />
                                </>
                                : null}
                            <Row>
                                <Col>
                                    <Form.Check.Label style={{ textTransform: "capitalize", fontSize: "14px" }} >{"preferred channel ?"}</Form.Check.Label> <span className="requiredField">&#42;</span><br/>
                                    {["phone", "video/chat"].map((val) => (
                                        <Form.Check style={{ textTransform: "capitalize", fontSize: "14px", marginRight: "65px"}} inline checked={formik.values.preferredChannel === val} type={"radio"} label={val} id={val} name="preferred channel"  onChange={e => formik.setFieldValue("preferredChannel", e.target.id)} />
                                     ))}
                                     {formik?.touched["preferredChannel"] && formik?.errors["preferredChannel"] ? (<div className="error-text">{formik?.errors["preferredChannel"]} </div>) : null}
                                </Col>
                            </Row>
                            <Row className="align-items-end">
                                <Col sm={8}>
                                    <TextInput keyField={"returnTelephoneNumber"} type="phone" className="appointment-phone-field" label={"Return Phone Number"} formik={formik} disabled={type === "inprogress" ? isTagPresent("TELEPHONE") ? true : false : false} placeholder={"Return Phone Number"} required={true} />
                                </Col>
                                <Col sm={4} md={3}>
                                    <TextInput keyField={"returnTelephoneNumberExt"} className="appointment-ext-field" label={"Ext."} formik={formik} disabled={type === "inprogress" ? isTagPresent("TELEPHONE") ? true : false : false} placeholder={"Ext."} required={false} />
                                </Col>
                            </Row>
                            
                            <Row></Row>
                            <Row>
                                {/* <Col>
                                    <TextArea keyField={"description"} label={"Description"} formik={formik} placeholder={"Description"} />
                                </Col> */}
                            </Row>
                        </>
                    )}                    
                    <div className="btn-wrap">
                        <Button onClick={() => handleHide()}  variant="secondary" title="Cancel">
                            Cancel
                        </Button>
                        <Button type="submit" isLoading={btnLoading}>
                            {type == "reschedule" ? "Reschedule" : "Create"}
                        </Button>
                    </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    )

};
export default Appointment;